import { Tabs } from "@mantine/core";
import { useNavigate } from "react-router";
import { usePermission } from "@how/app-kit";
import { CountBadge } from "@how/ui";
import { useInboxUnreadCount } from "./inbox-queries";
import { PilotTextingBanner } from "./PilotTextingBanner";

export type CommunicationsTab =
  "audiences" | "templates" | "campaigns" | "senders" | "inbox" | "text-us";

const PATHS: Record<CommunicationsTab, string> = {
  audiences: "/communications",
  templates: "/communications/templates",
  campaigns: "/communications/campaigns",
  senders: "/communications/senders",
  inbox: "/communications/inbox",
  "text-us": "/communications/text-us",
};

/**
 * The Communications section's tabs: who to reach, what to say, the sends themselves, who emails come from (Email
 * senders, managed by whoever may send, #402), and the texts people send back (the Inbox, for those who hold
 * communications.inbox, #314), and the website's Text us button that feeds it (ADR 0074, the same permission). Above them, while DCX Logic has pilot texting on for the church, the banner that says
 * texts go out from the pilot sender to opted-in pilot testers only (ADR 0072).
 */
export function CommunicationsTabs({ active }: { readonly active: CommunicationsTab }) {
  const navigate = useNavigate();
  const inbox = usePermission("communications.inbox");
  const unread = useInboxUnreadCount(inbox.data === true);
  const count = unread.data ?? 0;
  return (
    <>
      <PilotTextingBanner />
      <Tabs
        value={active}
        onChange={(value) => {
          if (value !== null && value in PATHS) void navigate(PATHS[value as CommunicationsTab]);
        }}
        mb="lg"
      >
        <Tabs.List>
          <Tabs.Tab value="audiences">Audiences</Tabs.Tab>
          <Tabs.Tab value="templates">Templates</Tabs.Tab>
          <Tabs.Tab value="campaigns">Campaigns</Tabs.Tab>
          <Tabs.Tab value="senders">Email senders</Tabs.Tab>
          {inbox.data === true || active === "inbox" ? (
            <Tabs.Tab
              value="inbox"
              {...(count > 0
                ? {
                    "aria-label": `Inbox, ${String(count)} unread`,
                    rightSection: <CountBadge count={count} />,
                  }
                : {})}
            >
              Inbox
            </Tabs.Tab>
          ) : null}
          {inbox.data === true || active === "text-us" ? (
            <Tabs.Tab value="text-us">Text us</Tabs.Tab>
          ) : null}
        </Tabs.List>
      </Tabs>
    </>
  );
}
